import { AxiosError } from 'axios';

import { PaginationListResponse } from '@/src/infrastructure/interfaces/IAutomation.interface';
import {
  GetIntegrationsListPayload,
  IntegrationDetail,
  IntegrationItem,
  IntegrationServiceParams,
  IntegrationStepsData,
} from '@/src/infrastructure/interfaces/IIntegrations.interface';
import { APIService } from '@/src/infrastructure/Protocol/APIService';

import { IIntegrationsService } from './IIntegrationsService';

import { DataResponse } from '@/modules/ContactsModule/types/Segments';
import {
  ExecutionsData,
  LogReport,
  LogReportPayload,
  LogReportStatus,
} from '@/modules/IntegrationsModule/types';

export class IntegrationService implements IIntegrationsService {
  private api: APIService;
  private apiFiles: APIService;

  private static executionsCache = new Map<string, DataResponse<ExecutionsData>>();

  private constructor(params: IntegrationServiceParams) {
    this.api = new APIService(params.url_ema_app_v2, params.jwt);
    this.apiFiles = new APIService('');
  }

  static create(params: IntegrationServiceParams): IIntegrationsService {
    return new IntegrationService(params);
  }

  static clearExecutionsCache(id?: number, bashId?: string): void {
    if (id && bashId) {
      const cacheKey = `${id}/${bashId}`;
      IntegrationService.executionsCache.delete(cacheKey);
    } else {
      IntegrationService.executionsCache.clear();
    }
  }

  async getIntegrationsList(
    filters: GetIntegrationsListPayload
  ): Promise<DataResponse<PaginationListResponse<IntegrationItem>>> {
    try {
      const response = await this.api.get<PaginationListResponse<IntegrationItem>>(
        '/api/v2.1/journeys/integrations',
        filters
      );
      return { success: response.status === 200, data: response.data };
    } catch (error) {
      console.error(error);
      return { success: false, data: { result: [], pager: { countPartial: 0, countTotal: 0 } } };
    }
  }

  async createIntegration(name: string): Promise<DataResponse<string | IntegrationItem>> {
    try {
      const response = await this.api.post<IntegrationItem>(`api/v2.1/journeys/integrations`, {
        name,
        version: 1,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error createIntegration:', error);
      if (error instanceof AxiosError) {
        if (error?.response?.status === 400) {
          return { success: false, data: error.response?.data.type };
        }
      }
      return { success: false, data: 'INTERNAL_ERROR' };
    }
  }

  async deleteIntegrations(ids: number[]): Promise<DataResponse<string>> {
    try {
      const response = await this.api.delete(`api/v2.1/journeys/integrations`, {
        journeyIds: ids,
      });
      return { success: true, data: 'SUCCESS' };
    } catch (error) {
      console.error('Error deleteIntegrations:', error);
      return { success: false, data: 'INTERNAL_ERROR' };
    }
  }

  async duplicateIntegration(name: string, id: number): Promise<DataResponse<string>> {
    try {
      await this.api.post(`api/v2.1/journeys/integrations/${id}/duplicate`, {
        name,
      });
      return { success: true, data: 'DUPLICATED' };
    } catch (error) {
      console.error('Error duplicateIntegration:', error);
      if (error instanceof AxiosError) {
        if (error.status === 400) {
          return { success: false, data: error.response?.data.type };
        }
      }
      return { success: false, data: 'INTERNAL_ERROR' };
    }
  }

  async renameIntegration(name: string, id: number): Promise<DataResponse<string>> {
    try {
      await this.api.patch(`api/v2.1/journeys/integrations/${id}`, {
        name,
      });
      return { success: true, data: 'RENAMED' };
    } catch (error) {
      console.error('Error renameIntegration:', error);
      if (error instanceof AxiosError) {
        if (error.status === 400) {
          return { success: false, data: error.response?.data.type };
        }
      }
      return { success: false, data: 'INTERNAL_ERROR' };
    }
  }

  async saveIntegration(
    id: number,
    stepsData: IntegrationStepsData
  ): Promise<DataResponse<string>> {
    try {
      await this.api.put(`api/v2.1/journeys/integrations/${id}`, stepsData);
      return { success: true, data: 'SAVED' };
    } catch (error) {
      console.error('Error saveIntegration:', error);
      return { success: false, data: 'INTERNAL_ERROR' };
    }
  }

  async getIntegration(id: number): Promise<DataResponse<IntegrationDetail>> {
    try {
      const response = await this.api.get<IntegrationDetail>(
        `api/v2.1/journeys/integrations/${id}`
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error getIntegration:', error);
      return { success: false, data: {} as IntegrationItem };
    }
  }

  async updateIntegrationStatus(id: number, status: boolean): Promise<DataResponse<string>> {
    try {
      await this.api.patch(
        `api/v2.1/journeys/integrations/${id}/${status ? 'activate' : 'deactivate'}`
      );
      return { success: true, data: 'UPDATED' };
    } catch (error) {
      console.error('Error updateIntegrationStatus:', error);
      return { success: false, data: 'INTERNAL_ERROR' };
    }
  }

  async getIntegrationReport(
    id: string,
    filters: LogReportPayload
  ): Promise<DataResponse<PaginationListResponse<LogReport>>> {
    try {
      const status = filters.status?.length === 1 ? filters.status[0] : undefined;
      const newFilters: Omit<LogReportPayload, 'status'> & { status?: LogReportStatus } = {
        ...filters,
        status,
      };
      const response = await this.api.get<PaginationListResponse<LogReport>>(
        `api/v2.1/journeys/integrations/${id}/reports/create_contacts`,
        newFilters
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error getIntegrationReport:', error);
      return { success: false, data: { result: [], pager: { countPartial: 0, countTotal: 0 } } };
    }
  }

  async getExecutions(id: number, bashId: string): Promise<DataResponse<ExecutionsData>> {
    const cacheKey = `${id}/${bashId}`;
    if (IntegrationService.executionsCache.has(cacheKey)) {
      const cachedResponse = IntegrationService.executionsCache.get(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }
    }

    try {
      const response = await this.api.get<ExecutionsData>(
        `api/v2.1/journeys/integrations/${id}/reports/create_contacts/${bashId}`
      );
      const result: DataResponse<ExecutionsData> = {
        success: true,
        data: response.data,
      };

      IntegrationService.executionsCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error getExecutions:', error);
      return { success: false, data: {} as ExecutionsData };
    }
  }
}
